import React, {
	CSSProperties,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./location-description.module.scss";
import { VelavuDevice, VelavuSite } from "velavu-js-api";
import { LocationDescriptionState } from "./use-location-description";
import { classArr } from "../../../helper/style-helper";
import { useCachedLocationDescription } from "./use-cached-location-description";

export interface LocationDescriptionProps {
	style?: CSSProperties;
	className?: string;
	textClassName?: string | null;
	device: VelavuDevice | undefined;
	site?: VelavuSite;
}

export default function LocationDescription(props: LocationDescriptionProps) {
	const { t } = useTranslation();

	//Look up the description
	const description = useCachedLocationDescription(props.device, props.site);

	//Map states
	let effectiveText: string[];
	if (description.state === LocationDescriptionState.OK) {
		effectiveText = description.lines;
	} else if (description.lines !== undefined) {
		effectiveText = description.lines;
	} else {
		switch (description.state) {
			case LocationDescriptionState.Loading:
				effectiveText = [t("api_helper.checking_location")];
				break;
			case LocationDescriptionState.Error:
				effectiveText = [t("api_helper.unknown_location")];
				break;
			case LocationDescriptionState.Unavailable:
				effectiveText = [t("api_helper.no_location")];
				break;
		}
	}
	const showLoadingAnimation =
		description.state === LocationDescriptionState.Loading;

	//Build the text class name
	const textClassName = classArr(
		//null means no class name, undefined means use the default
		props.textClassName === undefined ? styles.label : props.textClassName,
		showLoadingAnimation && styles.loadingLabel,
	);

	if (effectiveText.length === 1) {
		return (
			<SingleLineDescription
				className={props.className}
				textClassName={textClassName}
				text={effectiveText[0]}
			/>
		);
	} else {
		return (
			<MultilineDescription
				className={props.className}
				textClassName={textClassName}
				lines={effectiveText}
			/>
		);
	}
}

interface SingleLineDescriptionProps {
	className?: string;
	textClassName: string | undefined;
	text: string;
}

function SingleLineDescription(props: SingleLineDescriptionProps) {
	return (
		<span
			className={classArr(
				props.className,
				props.textClassName,
				styles.inlineLabel,
			)}
		>
			{props.text}
		</span>
	);
}

interface MultilineDescriptionProps {
	className?: string;
	textClassName: string | undefined;
	lines: string[];
}

function MultilineDescription(props: MultilineDescriptionProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLSpanElement>(null);

	const [useSingleLine, setUseSingleLine] = useState(false);

	useLayoutEffect(() => {
		//Measure if the single-line version fits
		const containerWidth = containerRef.current!.offsetWidth;
		const contentWidth = measureRef.current!.offsetWidth;

		setUseSingleLine(contentWidth < containerWidth);
	}, [props.lines]);

	const singleLineText = useMemo(() => {
		return props.lines.join(" • ");
	}, [props.lines]);

	return (
		<div
			className={classArr(props.className, styles.container)}
			ref={containerRef}
		>
			<span
				ref={measureRef}
				className={classArr(props.textClassName, styles.measure)}
			>
				{singleLineText}
			</span>

			{useSingleLine ? (
				<span className={props.textClassName}>{singleLineText}</span>
			) : (
				<div className={styles.multilineContainer}>
					{props.lines.map((line, index) => (
						<span
							key={index + " " + line}
							className={classArr(
								styles.multilineLabel,
								props.textClassName,
							)}
						>
							{line}
						</span>
					))}
				</div>
			)}
		</div>
	);
}
